import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  SearchOption,
  TypeaheadInput,
  TypeaheadInputComponent,
  Flex,
  Tag,
  Text,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Sizes } from "@jsq/ds/styles";
import { clientContext } from "#common/utils/graphqlContext";
import { useGetInvestorPositionsByEntitiesLazyQuery } from "#graphql/supergraphHooks";
import type { PositionSearchProps } from "./types";

/** Return an empty string if account is null (positions without an account are skipped). */
const safeAccountName = (account: { name: string } | null): string | null =>
  account?.name ?? null;

/**
 * Typeahead for investor positions. Fetches positions for the given arenaIds +
 * entityIds and filters client-side. Enabled only when both are non-empty.
 */
export const PositionSearch = (props: PositionSearchProps) => {
  const {
    arenaIds,
    entityIds,
    value: _value,
    values = [],
    multiselect = false,
    onSelect,
    onMultiSelect,
    onSearchClear,
    placeholder = "Search for a position",
    infoText = "Please select a client and entity first",
    className,
    disabled = false,
    isClearable = true,
    isFullWidth = true,
    testId = "position-search",
  } = props;

  const [fetchPositions, { data, loading }] = useGetInvestorPositionsByEntitiesLazyQuery();
  const componentRef = useRef<TypeaheadInputComponent>(null);
  const isMountedRef = useRef(false);
  const positionNamesCache = useRef<Record<string, string>>({});

  const effectiveArenaIds = useMemo(() => arenaIds ?? [], [arenaIds]);
  const effectiveEntityIds = useMemo(() => entityIds ?? [], [entityIds]);
  const isEnabled = effectiveArenaIds.length > 0 && effectiveEntityIds.length > 0;

  // Clear input when arenaIds or entityIds change (skip mount to avoid spurious onClearValue).
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    componentRef?.current?.clearInputValue();
  }, [arenaIds, entityIds]);

  // Fetch positions when enabled.
  useEffect(() => {
    if (isEnabled) {
      void fetchPositions({
        context: clientContext(),
        variables: { arenaIds: effectiveArenaIds, entityIds: effectiveEntityIds },
        fetchPolicy: "no-cache",
      });
    }
  }, [effectiveArenaIds, effectiveEntityIds, isEnabled, fetchPositions]);

  const positionOptions: SearchOption[] = useMemo(() => {
    const edges = data?.dataVaultEntities?.edges ?? [];
    const seen = new Set<string>();
    const list: SearchOption[] = [];

    for (const edge of edges) {
      for (const posEdge of edge.node.owningInvestorPositions?.edges ?? []) {
        const pos = posEdge.node;
        const accountName = safeAccountName(pos.account);
        if (!seen.has(pos.id) && accountName !== null) {
          seen.add(pos.id);
          const label = accountName;
          list.push({ id: pos.id, label });
          positionNamesCache.current[pos.id] = label;
        }
      }
    }

    return list.sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const selectedPositions = useMemo(() => {
    if (!multiselect || !values.length) return [];
    return values.map((id) => ({
      id,
      label:
        positionOptions.find((opt) => opt.id === id)?.label ??
        positionNamesCache.current[id] ??
        `Position ${id}`,
    }));
  }, [multiselect, values, positionOptions]);

  const resolvedSingleLabel = useMemo(() => {
    if (multiselect || !_value) return undefined;
    return (
      positionOptions.find((opt) => opt.id === _value)?.label ??
      positionNamesCache.current[_value]
    );
  }, [multiselect, _value, positionOptions]);

  const handleSelection = useCallback(
    (selectedOption?: SearchOption) => {
      if (!selectedOption?.id) return;
      const selectedId = selectedOption.id.toString();
      if (selectedOption.label) {
        positionNamesCache.current[selectedId] = selectedOption.label;
      }
      if (multiselect) {
        if (!onMultiSelect || values.includes(selectedId)) return;
        onMultiSelect([...values, selectedId]);
      } else {
        onSelect(selectedId);
      }
    },
    [onSelect, onMultiSelect, multiselect, values],
  );

  const handleRemoveTag = useCallback(
    (idToRemove: string) => {
      if (!onMultiSelect) return;
      onMultiSelect(values.filter((id) => id !== idToRemove));
    },
    [values, onMultiSelect],
  );

  const handleClearValue = useCallback(() => {
    if (!multiselect) onSearchClear?.();
  }, [multiselect, onSearchClear]);

  const infoRow = !isEnabled && (
    <Flex alignItems="center" gap={Sizes.XS}>
      <Icon type="exclamation-circle" size="small" />
      <Text>{infoText}</Text>
    </Flex>
  );

  if (multiselect) {
    return (
      <Flex flexDirection="column" gap={Sizes.SM}>
        {infoRow}
        {selectedPositions.length > 0 && (
          <Flex flexWrap="wrap" gap={Sizes.XS}>
            {selectedPositions.map((pos) => (
              <Tag
                key={pos.id}
                label={pos.label}
                onRemove={() => handleRemoveTag(pos.id)}
                data-testid={`${testId}-tag-${pos.id}`}
              />
            ))}
          </Flex>
        )}
        <TypeaheadInput
          className={className}
          data-testid={testId}
          ref={componentRef}
          isClearable={false}
          isFullWidth={isFullWidth}
          disabled={disabled || !isEnabled}
          isLoading={loading}
          options={positionOptions}
          onSelectOption={handleSelection}
          maxResultsLength={100}
          placeholder={placeholder}
        />
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap={Sizes.SM}>
      {infoRow}
      <TypeaheadInput
        key={_value ?? "empty"}
        className={className}
        data-testid={testId}
        ref={componentRef}
        isClearable={isClearable}
        isFullWidth={isFullWidth}
        disabled={disabled || !isEnabled}
        isLoading={loading}
        options={positionOptions}
        onSelectOption={handleSelection}
        onClearValue={handleClearValue}
        maxResultsLength={100}
        placeholder={placeholder}
        initialSearchValue={resolvedSingleLabel}
      />
    </Flex>
  );
};
